import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAddresses, updateAddress } from '@/api';
import AddAddressModal from './AddAddressModal';
import MapEditorModal, { formatShortAddress } from './MapEditorModal';
import {
  persistDeliveryAddress,
  readDeliveryAddress,
  clearDeliveryAddress,
  subscribeDeliveryAddress,
  getAddressTypeIcon,
} from '@/utils/deliveryAddress';

interface AddressItem {
  id: string;
  display: string;
  label: string;
  isDefault: boolean;
  latitude?: number | null;
  longitude?: number | null;
  type?: number;
}

interface AddressDateTimeBarProps {
  isDarkMode: boolean;
  inlineBar?: boolean;
}

export default function AddressDateTimeBar({ isDarkMode, inlineBar = true }: AddressDateTimeBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedAddressType, setSelectedAddressType] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const barRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(400);
  const [activeMode, setActiveMode] = useState<'address' | 'datetime'>('address');
  const addressBtnRef = useRef<HTMLButtonElement>(null);
  const dateTimeBtnRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pillStyle, setPillStyle] = useState({ left: 8, width: 140 });

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [showBarMapEditor, setShowBarMapEditor] = useState(false);
  const [editingBarAddr, setEditingBarAddr] = useState<AddressItem | null>(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const calc = () => {
      if (!wrapperRef.current) return;
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const targetRef = activeMode === 'address' ? addressBtnRef.current : dateTimeBtnRef.current;
      if (!targetRef) return;
      const targetRect = targetRef.getBoundingClientRect();
      setPillStyle({
        left: targetRect.left - wrapperRect.left,
        width: targetRect.width,
      });
    };
    requestAnimationFrame(calc);
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [activeMode, barWidth]);

  useEffect(() => {
    const update = () => {
      if (barRef.current) setBarWidth(barRef.current.offsetWidth);
      else setBarWidth(window.innerWidth - 32);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const apply = () => {
      const d = readDeliveryAddress();
      setSelectedAddress(d.display || '');
      setSelectedAddressId(d.id != null && d.id !== '' ? String(d.id) : null);
      setSelectedAddressType(d.type ?? null);
    };
    apply();
    return subscribeDeliveryAddress(apply);
  }, []);

  // Prefetch addresses so bar edit works without opening modal first
  useEffect(() => {
    if (!token) {
      // Guest: never show a stale saved address that isn't backed by an account list
      const d = readDeliveryAddress();
      if (d.display || d.id) {
        // Keep guest pin only if they explicitly set coords via map without account —
        // but if there's a leftover id from a previous login with empty meaning, clear orphaned display without coords
        if (d.id && (d.lat == null || d.lon == null)) {
          clearDeliveryAddress();
          setSelectedAddress('');
          setSelectedAddressId(null);
          setSelectedAddressType(null);
        }
      }
      return;
    }
    getAddresses()
      .then((data) => {
        if (!Array.isArray(data)) return;
        const mapped = data.map((addr: any) => {
          const parts = [addr.street, addr.city, addr.state, addr.zipCode || addr.zip, addr.country].filter(Boolean);
          return {
            id: String(addr.id),
            display: parts.join(', ') || addr.label || t('addresses.addressFallback'),
            label: addr.label || t('addresses.addressFallback'),
            isDefault: addr.isDefault || false,
            latitude: addr.latitude ?? null,
            longitude: addr.longitude ?? null,
            type: Number(addr.type ?? 0),
          };
        });
        setAddresses(mapped);

        if (mapped.length === 0) {
          clearDeliveryAddress();
          setSelectedAddress('');
          setSelectedAddressId(null);
          setSelectedAddressType(null);
          return;
        }

        const savedId = localStorage.getItem('selectedDeliveryAddressId');
        const match =
          (savedId && mapped.find((a) => String(a.id) === String(savedId))) ||
          mapped.find((a) => a.isDefault) ||
          mapped[0];

        if (savedId && !mapped.some((a) => String(a.id) === String(savedId))) {
          // Stale id from deleted addresses / Guid migration
          clearDeliveryAddress();
        }

        if (match) {
          persistDeliveryAddress({
            display: match.display,
            id: match.id,
            lat: match.latitude,
            lon: match.longitude,
            type: match.type,
          });
          setSelectedAddress(match.display);
          setSelectedAddressId(String(match.id));
          setSelectedAddressType(match.type ?? null);
        }
      })
      .catch(() => {});
  }, [token, t]);

  const handleSelectAddress = (
    display: string,
    addrId?: string | number,
    addrLat?: number,
    addrLon?: number,
    addrType?: number,
  ) => {
    persistDeliveryAddress({
      display,
      id: addrId,
      lat: addrLat,
      lon: addrLon,
      type: addrType ?? null,
    });
    setSelectedAddress(display);
    if (addrId != null && addrId !== '') setSelectedAddressId(String(addrId));
    if (addrType != null) setSelectedAddressType(addrType);
    setShowAddressModal(false);
  };

  const handleAddressesChange = (newAddresses: AddressItem[]) => {
    setAddresses(newAddresses);
    if (!newAddresses.length) {
      clearDeliveryAddress();
      setSelectedAddress('');
      setSelectedAddressId(null);
      setSelectedAddressType(null);
      return;
    }
    const savedId = localStorage.getItem('selectedDeliveryAddressId');
    if (savedId && !newAddresses.some((a) => String(a.id) === String(savedId))) {
      clearDeliveryAddress();
      setSelectedAddress('');
      setSelectedAddressId(null);
      setSelectedAddressType(null);
      return;
    }
    if (savedId) setSelectedAddressId(String(savedId));
  };

  const handleAddressSaved = (saved?: {
    id?: number;
    display: string;
    lat?: number | null;
    lon?: number | null;
    type?: number;
  }) => {
    const fetchAddrs = async () => {
      try {
        const data = await getAddresses();
        if (Array.isArray(data)) {
          const mapped: AddressItem[] = data.map((addr: any) => {
            const parts = [addr.street, addr.city, addr.state, addr.zipCode || addr.zip, addr.country].filter(Boolean);
            return {
              id: addr.id,
              display: parts.join(', ') || addr.label || t('addresses.addressFallback'),
              label: addr.label || t('addresses.addressFallback'),
              isDefault: addr.isDefault || false,
              latitude: addr.latitude ?? null,
              longitude: addr.longitude ?? null,
              type: Number(addr.type ?? 0),
            };
          });
          setAddresses(mapped);
          if (saved) {
            const match = mapped.find((a) => a.id === saved.id) || mapped[mapped.length - 1];
            if (match) {
              handleSelectAddress(
                saved.display || match.display,
                match.id,
                saved.lat ?? match.latitude ?? undefined,
                saved.lon ?? match.longitude ?? undefined,
                saved.type ?? match.type,
              );
            }
          }
        }
      } catch {
        /* silent */
      }
    };
    fetchAddrs();
  };

  const handleBarEdit = () => {
    if (!selectedAddressId) return;
    let addr = addresses.find((a) => a.id === selectedAddressId);
    if (!addr) {
      const d = readDeliveryAddress();
      addr = {
        id: selectedAddressId,
        display: d.display,
        label: d.display,
        isDefault: false,
        latitude: d.lat,
        longitude: d.lon,
        type: d.type ?? 0,
      };
    }
    setEditingBarAddr(addr);
    setShowBarMapEditor(true);
  };

  const handleBarMapConfirm = async (result: any) => {
    if (!editingBarAddr) return;
    try {
      const shortAddr = formatShortAddress(result);
      const a = result.address;
      const street = [a.road, a.house_number].filter(Boolean).join(' ') || shortAddr;
      const city = a.city || a.town || a.village || '';
      await updateAddress(editingBarAddr.id, {
        label: editingBarAddr.label,
        fullName: '',
        phone: '',
        street,
        city,
        state: a.state || a.state_district || '',
        zipCode: a.postcode || '',
        country: a.country || '',
        type: editingBarAddr.type ?? 0,
        isDefault: editingBarAddr.isDefault,
        latitude: parseFloat(result.lat) || null,
        longitude: parseFloat(result.lon) || null,
      });
      handleSelectAddress(
        shortAddr,
        editingBarAddr.id,
        parseFloat(result.lat),
        parseFloat(result.lon),
        editingBarAddr.type,
      );
      const data = await getAddresses();
      if (Array.isArray(data)) {
        setAddresses(
          data.map((addr: any) => {
            const parts = [addr.street, addr.city, addr.state, addr.zipCode || addr.zip, addr.country].filter(Boolean);
            return {
              id: addr.id,
              display: parts.join(', ') || addr.label || t('addresses.addressFallback'),
              label: addr.label || t('addresses.addressFallback'),
              isDefault: addr.isDefault || false,
              latitude: addr.latitude ?? null,
              longitude: addr.longitude ?? null,
              type: Number(addr.type ?? 0),
            };
          }),
        );
      }
      setShowBarMapEditor(false);
      setEditingBarAddr(null);
    } catch {
      /* silent */
    }
  };

  const timeSlots = [
    { label: '09:00 - 10:00', value: '09:00 - 10:00' },
    { label: '10:00 - 11:00', value: '10:00 - 11:00' },
    { label: '11:00 - 12:00', value: '11:00 - 12:00' },
    { label: '12:00 - 13:00', value: '12:00 - 13:00' },
    { label: '13:00 - 14:00', value: '13:00 - 14:00' },
    { label: '14:00 - 15:00', value: '14:00 - 15:00' },
    { label: '15:00 - 16:00', value: '15:00 - 16:00' },
    { label: '16:00 - 17:00', value: '16:00 - 17:00' },
    { label: '17:00 - 18:00', value: '17:00 - 18:00' },
    { label: '18:00 - 19:00', value: '18:00 - 19:00' },
    { label: '19:00 - 20:00', value: '19:00 - 20:00' },
    { label: '20:00 - 21:00', value: '20:00 - 21:00' },
  ];

  const quickDates = (() => {
    const today = new Date();
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const tomorrowDate = new Date(today);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const dayAfterDate = new Date(today);
    dayAfterDate.setDate(dayAfterDate.getDate() + 2);
    return [
      { label: t('datetime.today'), sub: `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}`, value: fmt(today) },
      { label: t('datetime.tomorrow'), sub: `${String(tomorrowDate.getDate()).padStart(2, '0')}/${String(tomorrowDate.getMonth() + 1).padStart(2, '0')}`, value: fmt(tomorrowDate) },
      { label: t('datetime.dayAfter'), sub: `${String(dayAfterDate.getDate()).padStart(2, '0')}/${String(dayAfterDate.getMonth() + 1).padStart(2, '0')}`, value: fmt(dayAfterDate) },
    ];
  })();

  useEffect(() => {
    const handleOpenAddress = () => setShowAddressModal(true);
    const handleOpenTime = () => setShowDateTimeModal(true);
    window.addEventListener('openAddressModal', handleOpenAddress);
    window.addEventListener('openDateTimeModal', handleOpenTime);
    return () => {
      window.removeEventListener('openAddressModal', handleOpenAddress);
      window.removeEventListener('openDateTimeModal', handleOpenTime);
    };
  }, []);

  return (
    <>
      {inlineBar && (
        <div className="max-w-7xl mx-auto px-3 md:px-4 pt-1.5 md:pt-2 pb-0 relative" ref={barRef}>
          <div ref={wrapperRef} className={`relative flex items-center gap-0 border rounded-[20px] border-[0.1px] border-stone-400/50 px-2 py-1.5 ${
            isDarkMode
              ? 'bg-stone-200/20'
              : 'bg-stone-200/20'
          } backdrop-blur-xl shadow-sm`}>
            {/* Animated sliding highlight pill */}
            <motion.div
              animate={{ left: pillStyle.left, width: pillStyle.width }}
              transition={{
                type: 'spring',
                stiffness: 150,
                damping: 20,
                mass: 1.2,
              }}
              className="absolute top-1 bottom-1 rounded-[18px] border-[0.1px] border-stone-400/50 bg-stone-300/15 shadow-sm pointer-events-none z-0"
            />
            {/* Address Box */}
            <button
              ref={addressBtnRef}
              onClick={() => { setActiveMode('address'); setShowAddressModal(true); }}
              className={`relative flex items-center gap-2 px-2 sm:px-3 py-2 rounded-[18px] transition-all duration-500 cursor-pointer max-w-[180px] sm:max-w-xs z-10 ${
                activeMode === 'address'
                  ? ''
                  : isDarkMode
                    ? 'border-[0.1px] border-transparent hover:bg-dark-200/20'
                    : 'border-[0.1px] border-transparent hover:bg-cream/20'
              }`}
            >
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                isDarkMode ? 'bg-gold/15' : 'bg-gold/10'
              }`}>
                <i className={`${getAddressTypeIcon(selectedAddressType, !!selectedAddress)} text-gold text-sm sm:text-base`}></i>
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>
                  {t('home.deliverTo')}
                </div>
                <div className={`text-xs font-medium truncate ${isDarkMode ? 'text-stone-200' : 'text-stone-800'}`}>
                  {selectedAddress ? selectedAddress.split(',')[0] : t('home.selectAddress')}
                </div>
              </div>
              <i className={`ri-arrow-down-s-line text-sm sm:text-base flex-shrink-0 ${
                isDarkMode ? 'text-stone-500' : 'text-stone-400'
              }`}></i>
            </button>

            {/* Edit button - only when address selected */}
            {selectedAddressId && (
              <button
                onClick={(e) => { e.stopPropagation(); handleBarEdit(); }}
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-all cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-dark-200/30 text-stone-400 hover:text-gold'
                    : 'hover:bg-stone-200/30 text-stone-400 hover:text-gold'
                }`}
                title={t('addresses.editPin')}
              >
                <i className="ri-pencil-line text-xs sm:text-sm"></i>
              </button>
            )}

            {/* Separator */}
            <div className={`w-px h-6 mx-1 z-10 ${isDarkMode ? 'bg-stone-500/30' : 'bg-stone-300/40'}`}></div>

            {/* DateTime Selector */}
            <button
              ref={dateTimeBtnRef}
              onClick={() => { setActiveMode('datetime'); setShowDateTimeModal(true); }}
              className={`relative flex items-center gap-2 px-2 sm:px-3 py-2 rounded-[18px] transition-all duration-500 cursor-pointer ml-auto z-10 ${
                activeMode === 'datetime'
                  ? ''
                  : isDarkMode
                    ? 'border-[0.1px] border-transparent hover:bg-dark-200/20'
                    : 'border-[0.1px] border-transparent hover:bg-cream/20'
              }`}
            >
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                isDarkMode ? 'bg-gold/15' : 'bg-gold/10'
              }`}>
                <i className="ri-calendar-line text-gold text-sm sm:text-base"></i>
              </div>
              <div className="text-left min-w-0 hidden sm:block">
                <div className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>
                  {t('home.schedule')}
                </div>
                <div className={`text-xs font-medium truncate ${isDarkMode ? 'text-stone-200' : 'text-stone-800'}`}>
                  {selectedDate && selectedTime
                    ? `${selectedDate} ${selectedTime}`
                    : t('home.selectDateTime')
                  }
                </div>
              </div>
              <i className={`ri-arrow-down-s-line text-sm sm:text-base flex-shrink-0 ${
                isDarkMode ? 'text-stone-500' : 'text-stone-400'
              }`}></i>
            </button>
          </div>

          {/* Bottom arc decoration */}
          <svg
            className="absolute left-0 right-0 -bottom-1 pointer-events-none"
            width={barWidth}
            height={16}
            viewBox={`0 0 ${barWidth} 16`}
            preserveAspectRatio="none"
          >
            <path
              d={`M 0 2 Q ${barWidth / 2} 16 ${barWidth} 2`}
              fill="none"
              stroke={isDarkMode ? 'rgba(202,165,62,0.35)' : 'rgba(202,165,62,0.3)'}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <div
            className="absolute left-4 right-4 -bottom-1 h-3 rounded-full blur-md pointer-events-none"
            style={{
              background: isDarkMode
                ? 'rgba(202,165,62,0.06)'
                : 'rgba(202,165,62,0.08)',
            }}
          />
        </div>
      )}

      {/* Unified Address Modal */}
      <AddAddressModal
        isDarkMode={isDarkMode}
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSelectAddress={handleSelectAddress}
        onAddressSaved={handleAddressSaved}
        addresses={addresses}
        onAddressesChange={handleAddressesChange}
        token={token}
        selectedAddressId={selectedAddressId}
      />

      {/* DateTime Modal */}
      {showDateTimeModal && (
        <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDateTimeModal(false)}></div>
          <div className={`relative w-full md:max-w-lg rounded-t-3xl md:rounded-3xl ${
            isDarkMode ? 'bg-dark-100/80 border-dark-200/30 backdrop-blur-xl' : 'bg-white/80 border-stone-200/30 backdrop-blur-xl'
          } border-t md:border shadow-2xl max-h-[80vh] overflow-hidden`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? 'border-dark-200/50' : 'border-stone-200/50'}`}>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-stone-100' : 'text-stone-900'}`}>{t('home.scheduleDelivery')}</h3>
              <button onClick={() => setShowDateTimeModal(false)} className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-dark-200/30 hover:bg-dark-300/40 border border-dark-200/20 text-stone-300' : 'bg-white/30 hover:bg-white/45 border border-stone-200/20 text-stone-600'} transition-all duration-300 cursor-pointer`}>
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-stone-300' : 'text-stone-700'}`}>{t('home.selectDate')}</label>
                <div className="flex items-stretch gap-2">
                  {quickDates.map((date) => (
                    <button
                      key={date.value}
                      onClick={() => {
                        setSelectedDate(date.value);
                      }}
                      className={`flex-1 px-3 py-3 rounded-xl text-center border transition-all duration-300 cursor-pointer ${
                        selectedDate === date.value
                          ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white border-transparent'
                          : isDarkMode ? 'bg-dark-100/35 hover:bg-dark-200/40 border-dark-200/25 text-stone-100' : 'bg-white/30 hover:bg-white/45 border-stone-300/25 text-stone-900'
                      }`}
                    >
                      <div className="text-sm font-medium whitespace-nowrap">{date.label}</div>
                      <div className={`text-xs mt-0.5 ${selectedDate === date.value ? 'text-white/70' : isDarkMode ? 'text-stone-500' : 'text-stone-500'}`}>{date.sub}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-stone-300' : 'text-stone-700'}`}>{t('home.selectTimeSlot')}</label>
                <div className="flex flex-col gap-2">
                  {timeSlots.map((time) => (
                    <button
                      key={time.value}
                      onClick={() => setSelectedTime(time.value)}
                      className={`w-full px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-300 whitespace-nowrap cursor-pointer text-left ${
                        selectedTime === time.value
                          ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white border-transparent'
                          : isDarkMode ? 'bg-dark-100/35 hover:bg-dark-200/40 border-dark-200/25 text-stone-100' : 'bg-white/30 hover:bg-white/45 border-stone-300/25 text-stone-900'
                      }`}
                    >
                      <i className={`ri-time-line mr-2 text-sm ${selectedTime === time.value ? '' : 'text-gold'}`}></i>
                      {time.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => {
                  if (selectedDate && selectedTime) {
                    localStorage.setItem('selectedDeliveryDate', selectedDate);
                    localStorage.setItem('selectedDeliveryTime', selectedTime);
                    setShowDateTimeModal(false);
                  }
                }}
                disabled={!selectedDate || !selectedTime}
                className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                  selectedDate && selectedTime
                    ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white hover:shadow-lg hover:shadow-gold/50'
                    : isDarkMode ? 'bg-dark-100/30 text-stone-500 cursor-not-allowed border border-dark-200/15' : 'bg-white/25 text-stone-400 cursor-not-allowed border border-stone-200/15'
                }`}
              >
                {t('home.confirmSchedule')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Editor for Bar */}
      <MapEditorModal
        isDarkMode={isDarkMode}
        isOpen={showBarMapEditor}
        onClose={() => { setShowBarMapEditor(false); setEditingBarAddr(null); }}
        initialLat={editingBarAddr?.latitude ? String(editingBarAddr.latitude) : '52.52'}
        initialLon={editingBarAddr?.longitude ? String(editingBarAddr.longitude) : '13.405'}
        initialAddress={editingBarAddr?.display || ''}
        addressType={editingBarAddr?.type ?? selectedAddressType}
        onConfirm={handleBarMapConfirm}
      />
    </>
  );
}